/**
 * Teslyn Cloud's "Project details" on a project's Set-up tab (R209; docs/specs/project-details.md; help
 * `cloud.project-setup`): the survey number (read only) and the title, client name, area and state, each with one
 * line saying where it is used, and Edit for the project's writers (tenant admins, managers, office staff: policy
 * `project_write`). The client name is free text, separate from the linked client organisation. A save is audited
 * by the database and reaches Teslyn Field with the next sync; the project folder on the laptop keeps its name.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PROJECT_DETAILS_FIELDS,
  PROJECT_DETAIL_LABEL,
  PROJECT_DETAIL_USES,
  cleanProjectDetails,
  projectDetailsBreachName,
  projectDetailsChanged,
  projectDetailsHints,
  projectDetailsIssues,
  projectDetailsOf,
  projectDetailsReadOnlyWhy,
  projectDetailsSavedWords,
  type ProjectDetails,
  type ProjectDetailsField,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import { loadProjectDetails, saveProjectDetails, type ProjectDetailsRow } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function ProjectDetailsPanel({
  sb,
  project,
  roles,
  onSaved,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  /** After a save: the page reloads the project so its heading shows the new title. */
  readonly onSaved?: () => void;
}): ReactElement {
  const [row, setRow] = useState<ProjectDetailsRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProjectDetails | null>(null);
  const [errors, setErrors] = useState<Partial<Record<ProjectDetailsField, string>>>({});
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setError(null);
    loadProjectDetails(sb, project.id)
      .then(setRow)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  const title = 'Project details';
  if (error)
    return (
      <section className="panel stack" aria-label={title}>
        <h2 className="heading">{title}</h2>
        <ErrorState message={error} onRetry={reload} />
      </section>
    );
  if (!row)
    return (
      <section className="panel stack" aria-label={title}>
        <h2 className="heading">{title}</h2>
        <Loading what="the project details" />
      </section>
    );

  const readOnlyWhy = projectDetailsReadOnlyWhy(roles, { localWorkspace: false, status: row.status });
  const stored = projectDetailsOf(row);
  const shown = draft ?? stored;
  const hints = draft ? projectDetailsHints(row.number, draft) : [];

  async function save(d: ProjectDetails): Promise<void> {
    setNotice(null);
    const issues = projectDetailsIssues(d);
    setErrors(Object.fromEntries(issues.map((i) => [i.field, i.message])));
    if (issues.length > 0) return;
    const clean = cleanProjectDetails(d);
    const changed = projectDetailsChanged(stored, clean);
    if (changed.length === 0) {
      setDraft(null);
      setNotice({ ok: true, text: projectDetailsSavedWords([]) });
      return;
    }
    setBusy(true);
    try {
      await saveProjectDetails(sb, project.id, clean);
      setRow({ ...row!, ...clean });
      setDraft(null);
      setNotice({ ok: true, text: projectDetailsSavedWords(changed) });
      onSaved?.();
    } catch (e) {
      setNotice({ ok: false, text: `Not saved. ${message(e)}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack" aria-label={title}>
      <div className="row row--wrap">
        <h2 className="heading">{title}</h2>
        {!draft && readOnlyWhy === null && (
          <Button
            onClick={() => {
              setNotice(null);
              setErrors({});
              setDraft(stored);
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <p className="caption muted">
        Changing these never renames the project folder on the laptop; Teslyn Field shows them after its next
        sync. The client name is the words on reports; the client organisation above decides who sees the
        project in the client portal.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <dl className="stack">
        <div>
          <dt className="label muted">{PROJECT_DETAIL_LABEL.number}</dt>
          <dd className="data">{row.number}</dd>
          <dd className="caption muted">{PROJECT_DETAIL_USES.number}</dd>
        </div>
        {!draft &&
          PROJECT_DETAILS_FIELDS.map((f) => (
            <div key={f}>
              <dt className="label muted">{PROJECT_DETAIL_LABEL[f]}</dt>
              <dd className="body">{stored[f] || 'Not set'}</dd>
              <dd className="caption muted">{PROJECT_DETAIL_USES[f]}</dd>
            </div>
          ))}
      </dl>
      {draft && (
        <form
          className="stack"
          aria-label="Edit the project details"
          onSubmit={(e) => {
            e.preventDefault();
            void save(draft);
          }}
        >
          {PROJECT_DETAILS_FIELDS.map((f) => (
            <TextField
              key={f}
              label={PROJECT_DETAIL_LABEL[f]}
              value={draft[f]}
              onChange={(v) => setDraft((d) => (d ? { ...d, [f]: v } : d))}
              hint={PROJECT_DETAIL_USES[f]}
              error={errors[f]}
              {...(f === 'title' ? { autoFocus: true } : {})}
            />
          ))}
          {hints.map((h) => (
            <p key={h} className="caption" role="note">
              {h}
            </p>
          ))}
          <div className="row">
            <Button variant="primary" busy={busy} onClick={() => void save(draft)}>
              Save project details
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setDraft(null);
                setErrors({});
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      <p className="caption">{projectDetailsBreachName(row, shown)}</p>
      {readOnlyWhy !== null && (
        <p className="caption muted" role="note">
          {readOnlyWhy}
        </p>
      )}
    </section>
  );
}
